import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { ShopService } from 'src/app/services/shop.service';
import { AuthService } from 'src/app/services/auth.service';
import { AccessData } from 'src/app/models/access-data.model';
import { Profile } from 'src/app/models/profile.model';
import { FormGroupDirective } from '@angular/forms';
import { Address } from 'src/app/models/address.model';
import { CreditCard } from 'src/app/models/credit-card.model';
import { Angulartics2 } from 'angulartics2';

@Component({
  selector: 'app-basket',
  templateUrl: './basket.component.html',
  styleUrls: ['./basket.component.scss']
})
export class BasketComponent implements OnInit, OnDestroy {

  step = 1;
  timeOpen = false;
  timeMissingError = false;
  orderType$: any;
  orderType = null;
  selectedDateTime: string;
  selectedDateTime$: any;
  userType: string; // guest, registered
  paymentType: string; // cash, card
  paymentTypeMissingError = false;
  address: Address = new Address();
  card: CreditCard = new CreditCard();
  user = {
    email: null,
    password: null
  };
  invalidCredentials = false;
  loggingIn = false;
  accessData: AccessData;
  profile: Profile;
  profile$: any;
  createOrderError: any;
  creatingOrder = false;
  calculating = false;

  @ViewChild('loginForm') documentEditForm: FormGroupDirective;

  constructor(
    private shop: ShopService,
    private auth: AuthService,
    private angulartics2: Angulartics2
  ) { }

  ngOnInit() {
    this.checkLocalStorageForAccessData();
    this.selectedDateTime$ = this.shop.selectedDateTime.subscribe(
      (s) => {
        if (s !== this.selectedDateTime) {
          this.timeOpen = false;
          this.selectedDateTime = s;
          this.timeMissingError = false;
        }
      }
    );
    this.orderType$ = this.shop.orderType.subscribe(
      (o) => {
        this.orderType = o;
      }
    );
    this.shop.getOpenDateTimes().subscribe(
      (d) => {
        // done
      }
    );
    this.profile$ = this.auth.profile.subscribe(
      (p) => {
        this.profile = p;
        this.fillAddressFromProfile();
      }
    );
  }

  ngOnDestroy() {
    this.orderType$.unsubscribe();
    this.selectedDateTime$.unsubscribe();
    this.profile$.unsubscribe();
  }

  toggleTime() {
    this.timeOpen = !this.timeOpen;
  }

  nextStep() {
    const order = this.shop.order.value;
    this.timeMissingError = false;
    this.paymentTypeMissingError = false;
    if (this.step === 1 && !this.selectedDateTime) {
      this.timeOpen = true;
      this.timeMissingError = true;
      return;
    }
    if (this.step === 2) {
      order.address = this.address;
      order.message = this.address.message;
      order.personsAmount = this.address.amountPersons;
    }
    if (this.step === 3) {
      if (!this.paymentType) {
        this.paymentTypeMissingError = true;
        return;
      }
      order.paymentInfo = this.card;
      this.shop.card.next(this.card);
    }
    this.shop.order.next(order);
    this.calculating = true;
    this.shop.calculateOrder().subscribe((calc) => {
      this.calculating = false;
      if (this.step === 3) {
        this.createOrderError = null;
        this.creatingOrder = true;
        this.shop.sendOrder().subscribe((success) => {
          this.creatingOrder = false;
          this.step += 1;
          this.shop.card.next(null);
          this.angulartics2.eventTrack.next({ action: 'orderSuccess' });
        }, (err) => {
          this.creatingOrder = false;
          this.createOrderError = err;
          this.angulartics2.eventTrack.next({ action: 'orderError' });
        });
      } else {
        this.step += 1;
      }
    }, (err) => {
      this.calculating = false;
    });
  }

  previousStep() {
    if (this.step === 1) { return; }
    this.step -= 1;
  }

  isValidEmail(email) {
    let isValid = false;
    const mailRegex = /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
    if (mailRegex.test(email.toString())) {
      isValid = true;
    }
    return isValid;
  }

  login() {
    this.invalidCredentials = false;
    this.loggingIn = true;
    this.auth.login({username: this.user.email, password: this.user.password}).subscribe(
      (resp) => {
        this.loggingIn = false;
        this.accessData = resp;
      }, (err) => {
        this.invalidCredentials = true;
        this.loggingIn = false;
      }
    );
  }

  checkLocalStorageForAccessData() {
    if (localStorage.getItem('accessData')) {
      const accessData: AccessData = JSON.parse(localStorage.getItem('accessData'));
      this.auth.accessData.next(accessData);
      this.accessData = accessData;
      this.userType = 'registered';
      if (this.profile) {
        this.fillAddressFromProfile();
      } else {
        this.auth.getProfile().subscribe(() => {},
        (err) => { this.accessData = null; });
      }
    }
  }

  fillAddressFromProfile() {
    if (!this.profile) { return; }
    this.address = {
      company: this.profile.company,
      sex: this.profile.sex, // 0 female, 1 male
      title: null,
      firstName: this.profile.firstName,
      lastName: this.profile.lastName,
      street: this.profile.street,
      zip: this.profile.zip,
      city: this.profile.city,
      country: null,
      email: this.profile.email,
      phone: this.profile.phone,
      mobilePhone: null,
      newsletter: this.profile.infoMailAllowed,
      amountPersons: 1
    };
    const order = this.shop.order.value;
    order.customerId = this.profile.customerId;
    this.shop.order.next(order);
    this.shop.calculateOrder().subscribe((r) => { });
  }

  onUserTypeChange() {
    if (this.userType === 'guest') {
      for (const property in this.address) {
        if (this.address.hasOwnProperty(property)) {
          this.address[property] = null;
        }
      }
      this.address.amountPersons = 1;
    }
    if (this.userType === 'registered') {
      this.checkLocalStorageForAccessData();
    }
  }

  onPaymentTypeChange() {
    if (this.paymentType) {
      const order = this.shop.order.value;
      order.paymentMethodId = this.paymentType === 'cash' ? 1 : 2;
      this.shop.order.next(order);
      this.paymentTypeMissingError = false;
    }
  }

  getContentMargin() {
    return window.innerWidth > 1149 ? '30px' : ' 90px';
  }

}
